import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Edit, ExternalLink, Eye, EyeOff, Link2, Loader2, Package, Plus, Sparkles, Trash2, Wand2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import ConfirmModal from '@/components/ConfirmModal';
import FeaturedSectionFormModal from '@/components/admin/FeaturedSectionFormModal';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import DeckFormModal from '@/components/admin/DeckFormModal';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
export default function AdminFeaturedSections() {
  const location = useLocation();
  const [decks, setDecks] = useState([]);
  const [featuredSections, setFeaturedSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDeckModal, setDeleteDeckModal] = useState({ isOpen: false, deckId: null });
  const [deleteFeaturedModal, setDeleteFeaturedModal] = useState({ isOpen: false, sectionId: null });
  const [previewDeck, setPreviewDeck] = useState(null); // For deck preview
  
  // Deck creation
  const [showDeckDialog, setShowDeckDialog] = useState(false);
  const [selectedProductsForDeck, setSelectedProductsForDeck] = useState([]);
  const [deckFormData, setDeckFormData] = useState({
    title: '',
    description: ''
  });
  
  // Featured section creation
  const [showFeaturedDialog, setShowFeaturedDialog] = useState(false);
  const [editingFeatured, setEditingFeatured] = useState(null);
  const [featuredFormData, setFeaturedFormData] = useState({
    name: '',
    description: '',
    category_name: '',
    order: 2
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // AI Create Featured Section
  const [showAIFeatured, setShowAIFeatured] = useState(false);
  const [aiFeaturedBrief, setAiFeaturedBrief] = useState('');
  const [aiFeaturedLoading, setAiFeaturedLoading] = useState(false);

  // Multi-select for decks and featured sections
  const [selectedDecks, setSelectedDecks] = useState([]);
  const [selectedFeaturedSections, setSelectedFeaturedSections] = useState([]);
  const [bulkDeleteDecksModal, setBulkDeleteDecksModal] = useState(false);
  const [bulkDeleteFeaturedModal, setBulkDeleteFeaturedModal] = useState(false);
  
  // Tab state for switching between Decks and Featured Sections
  const [activeTab, setActiveTab] = useState('decks'); // 'decks' or 'featured'

  useEffect(() => {
    fetchData();
  }, []);

  // Check if we should auto-open the create modal
  useEffect(() => {
    if (location.state?.openCreateModal && !loading) {
      resetFeaturedForm();
      setShowFeaturedDialog(true);
      // Clear the state to prevent reopening on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loading]);

  // Display sections that appear on Browse page
  const browseSections = [
    { name: 'For the Tech Enthusiast', description: 'Gadgets and gear for the digitally savvy' },
    { name: 'Home Sweet Home', description: 'Comfort and style for their space' },
    { name: 'For the Chef', description: 'Culinary delights and kitchen essentials' },
    { name: 'For the Bookworms', description: 'Literary treasures and reading companions' },
    { name: 'For the Artist', description: 'Creative tools and artistic inspiration' },
    { name: 'Self-Care Essentials', description: 'For mind, body, and soul' },
    { name: 'Fashion Forward', description: 'Style that makes a statement' },
    { name: 'Foodie Favorites', description: 'For the culinary connoisseur' }
  ];

  const fetchData = async () => {
    try {
      const headers = { };
      
      const [productsData, decksData, featuredData] = await Promise.all([
        fetch(`${api}/admin/products`, { headers }).then(r => r.json()),
        fetch(`${api}/admin/decks`, { headers }).then(r => r.json()),
        fetch(`${api}/admin/featured-sections`, { headers }).then(r => r.json()).catch(() => [])
      ]);
      
      setProducts(productsData.filter(p => p.is_active));
      setDecks(decksData);
      setFeaturedSections(featuredData);
    } catch (error) {
      toast.error('Failed to fetch data');
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ===== DECK CREATION =====
  const handleProductToggleForDeck = (productId) => {
    setSelectedProductsForDeck(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else if (prev.length < 5) {
        return [...prev, productId];
      } else {
        toast.error('Maximum 5 products per deck');
        return prev;
      }
    });
  };

  const handleCreateDeck = async () => {
    if (!deckFormData.title.trim()) {
      toast.error('Deck title is required');
      return;
    }
    
    if (selectedProductsForDeck.length < 2 || selectedProductsForDeck.length > 4) {
      toast.error('Deck must contain 2-4 products');
      return;
    }

    try {
      const response = await fetch(`${api}/admin/decks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: deckFormData.title,
          description: deckFormData.description,
          section_id: 'standalone',
          product_ids: selectedProductsForDeck,
          is_active: true
        })
      });

      if (!response.ok) throw new Error('Failed to create deck');

      toast.success('Deck created successfully!');
      setShowDeckDialog(false);
      setDeckFormData({ title: '', description: '' });
      setSelectedProductsForDeck([]);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteDeck = async (deckId) => {
    try {
      const response = await fetch(`${api}/admin/decks/${deckId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete deck');

      toast.success('Deck deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleDeck = async (deckId) => {
    try {
      const response = await fetch(`${api}/admin/decks/${deckId}/toggle`, {
        method: 'PATCH',
      });

      if (!response.ok) throw new Error('Failed to toggle deck');

      const data = await response.json();
      toast.success(data.is_active ? 'Deck activated' : 'Deck deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ===== FEATURED SECTIONS =====
  const handleItemToggle = (itemId, itemType) => {
    const idx = selectedItems.indexOf(itemId);
    
    if (idx > -1) {
      // Remove item
      setSelectedItems(prev => prev.filter((_, i) => i !== idx));
      setSelectedItemTypes(prev => prev.filter((_, i) => i !== idx));
    } else {
      // Add item
      if (selectedItems.length >= 8) {
        toast.error('Maximum 8 items per featured section');
        return;
      }
      setSelectedItems(prev => [...prev, itemId]);
      setSelectedItemTypes(prev => [...prev, itemType]);
    }
  };

  const handleAIFeatured = async () => {
    if (aiFeaturedBrief.trim().length < 3) {
      toast.error('Describe the theme first (e.g. "cozy fall evenings")');
      return;
    }
    setAiFeaturedLoading(true);
    try {
      const res = await fetch(`${api}/admin/ai/suggest-featured-section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brief: aiFeaturedBrief.trim(), sections: browseSections.map((s) => s.name) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'AI curation failed');
      setEditingFeatured(null);
      setFeaturedFormData({
        name: data.name || '',
        description: data.description || '',
        category_name: data.category_name || '',
        order: 2,
      });
      setSelectedItems(data.item_ids || []);
      setSelectedItemTypes(data.item_types || []);
      setShowItemSelector(false);
      setShowAIFeatured(false);
      setShowFeaturedDialog(true);
      toast.success(`✨ "${data.name}" drafted with ${(data.item_ids || []).length} items. Review and save.`);
    } catch (err) {
      logger.error('AI featured suggestion failed:', err);
      toast.error(err.message || 'AI suggestion failed');
    } finally {
      setAiFeaturedLoading(false);
    }
  };

  const handleCreateFeaturedSection = async () => {
    if (!featuredFormData.name.trim()) {
      toast.error('Section name is required');
      return;
    }
    
    if (!featuredFormData.category_name) {
      toast.error('Please select a category');
      return;
    }
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    try {
      const url = editingFeatured 
        ? `${api}/admin/featured-sections/${editingFeatured.id}`
        : `${api}/admin/featured-sections`;
      
      const response = await fetch(url, {
        method: editingFeatured ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: featuredFormData.name,
          description: featuredFormData.description,
          category_name: featuredFormData.category_name,
          order: featuredFormData.order || 2,
          item_ids: selectedItems,
          item_types: selectedItemTypes,
          is_active: true
        })
      });

      if (!response.ok) throw new Error('Failed to save featured section');

      toast.success(editingFeatured ? 'Featured section updated' : 'Featured section created');
      setShowFeaturedDialog(false);
      resetFeaturedForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditFeatured = (section) => {
    setEditingFeatured(section);
    setFeaturedFormData({
      name: section.name,
      description: section.description,
      category_name: section.category_name,
      order: section.order || 2
    });
    setSelectedItems(section.item_ids || []);
    setSelectedItemTypes(section.item_types || []);
    setShowFeaturedDialog(true);
  };

  const handleDeleteFeatured = async (sectionId) => {
    try {
      const response = await fetch(`${api}/admin/featured-sections/${sectionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete featured section');

      toast.success('Featured section deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleFeatured = async (sectionId) => {
    try {
      const response = await fetch(`${api}/admin/featured-sections/${sectionId}/toggle`, {
        method: 'PATCH',
      });

      if (!response.ok) throw new Error('Failed to toggle featured section');

      const data = await response.json();
      toast.success(data.is_active ? 'Section activated' : 'Section deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetFeaturedForm = () => {
    setEditingFeatured(null);
    setFeaturedFormData({
      name: '',
      description: '',
      category_name: '',
      order: 2
    });
    setSelectedItems([]);
    setSelectedItemTypes([]);
  };

  // Multi-select handlers
  const toggleSelectDeck = (deckId) => {
    setSelectedDecks(prev => 
      prev.includes(deckId)
        ? prev.filter(id => id !== deckId)
        : [...prev, deckId]
    );
  };

  const toggleSelectAllDecks = () => {
    if (selectedDecks.length === decks.length) {
      setSelectedDecks([]);
    } else {
      setSelectedDecks(decks.map(d => d.id));
    }
  };

  const toggleSelectFeaturedSection = (sectionId) => {
    setSelectedFeaturedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleSelectAllFeaturedSections = () => {
    if (selectedFeaturedSections.length === featuredSections.length) {
      setSelectedFeaturedSections([]);
    } else {
      setSelectedFeaturedSections(featuredSections.map(s => s.id));
    }
  };

  const handleBulkDeleteDecks = async () => {
    setBulkDeleteDecksModal(false);
    
    try {
      const headers = { 
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${api}/admin/decks/bulk-delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: selectedDecks })
      });
      
      if (!response.ok) throw new Error('Bulk delete failed');
      
      const result = await response.json();
      toast.success(result.message || `${selectedDecks.length} decks deleted`);
      setSelectedDecks([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete decks');
      logger.error(error);
    }
  };

  const handleBulkDeleteFeaturedSections = async () => {
    setBulkDeleteFeaturedModal(false);
    
    try {
      const headers = { 
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${api}/admin/featured-sections/bulk-delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: selectedFeaturedSections })
      });
      
      if (!response.ok) throw new Error('Bulk delete failed');
      
      const result = await response.json();
      toast.success(result.message || `${selectedFeaturedSections.length} featured sections deleted`);
      setSelectedFeaturedSections([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete featured sections');
      logger.error(error);
    }
  };

  const handleBulkToggleFeaturedSections = async (activate) => {
    try {
      const headers = { 
        'Content-Type': 'application/json'
      };
      
      const endpoint = activate ? 'bulk-activate' : 'bulk-deactivate';
      const response = await fetch(`${api}/admin/featured-sections/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: selectedFeaturedSections })
      });
      
      if (!response.ok) throw new Error('Bulk toggle failed');
      
      const result = await response.json();
      toast.success(result.message || `${selectedFeaturedSections.length} featured sections ${activate ? 'activated' : 'deactivated'}`);
      setSelectedFeaturedSections([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update featured sections');
      logger.error(error);
    }
  };

  const getItemName = (itemId, itemType) => {
    if (itemType === 'product') {
      const product = products.find(p => p.id === itemId);
      return product ? product.name : 'Unknown Product';
    } else {
      const deck = decks.find(d => d.id === itemId);
      return deck ? deck.title : 'Unknown Deck';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-300 rounded w-1/4"></div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#20212B' }}>Content Curation</h1>
        <p className="text-sm sm:text-base text-gray-600">Create decks and featured sections for the browse page</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 bg-white rounded-xl p-1 sm:p-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        <div className="grid grid-cols-2 gap-1 sm:gap-2">
          <button
            onClick={() => setActiveTab('decks')}
            className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
            style={{
              background: activeTab === 'decks' ? '#2D4354' : 'transparent',
              color: activeTab === 'decks' ? 'white' : '#534145'
            }}
          >
            <Package className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            Decks ({decks.length})
          </button>
          <button
            onClick={() => setActiveTab('featured')}
            className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
            style={{
              background: activeTab === 'featured' ? '#9E6752' : 'transparent',
              color: activeTab === 'featured' ? 'white' : '#534145'
            }}
          >
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            Featured ({featuredSections.length})
          </button>
        </div>
      </div>

      {/* ===== DECKS SECTION ===== */}
      {activeTab === 'decks' && (
        <div className="bg-white rounded-xl p-4 sm:p-6" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
                <Package className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#9E6752' }} />
                Decks ({decks.length})
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Bundles of 2-4 products</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {decks.length > 0 && (
                <Button
                  onClick={toggleSelectAllDecks}
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm hover:bg-[#FED7A5] hover:text-[#20212B] hover:border-[#9E6752] transition-colors hover-amber"
                  style={{ color: '#534145', borderColor: '#534145' }}
                >
                  {selectedDecks.length === decks.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
              <Button
                onClick={() => {
                  setDeckFormData({ title: '', description: '' });
                  setSelectedProductsForDeck([]);
                  setShowDeckDialog(true);
                }}
                style={{ background: '#2D4354', color: 'white' }}
                className="text-xs sm:text-sm px-3 sm:px-4"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Craft Deck
              </Button>
            </div>
          </div>

        {/* Bulk Actions Toolbar for Decks */}
        {selectedDecks.length > 0 && (
          <div className="mb-4 px-4 py-3 bg-[#FFF8F0] border border-[#FED7A5] rounded-lg flex items-center justify-between">
            <span className="text-sm font-medium text-[#2D4354]">
              {selectedDecks.length} deck{selectedDecks.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setSelectedDecks([])}
                variant="outline"
                size="sm"
              >
                Clear
              </Button>
              <Button
                onClick={() => setBulkDeleteDecksModal(true)}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete All
              </Button>
            </div>
          </div>
        )}

        {decks.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg" style={{ borderColor: '#E5E5E5' }}>
            <Package className="w-12 h-12 mx-auto mb-3" style={{ color: '#9E6752', opacity: 0.5 }} />
            <p className="text-gray-600">No decks created yet</p>
            <p className="text-sm text-gray-500">Create your first deck to bundle products together</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {decks.map(deck => (
              <div key={deck.id} className={`border-2 rounded-lg p-4 transition-all ${selectedDecks.includes(deck.id) ? 'border-[#9E6752] bg-[#FFF8F0]' : 'border-gray-200 hover:border-[#9E6752]'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedDecks.includes(deck.id)}
                      onChange={() => toggleSelectDeck(deck.id)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <h3 className="font-bold text-lg" style={{ color: '#20212B' }}>{deck.title}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleToggleDeck(deck.id)}
                      className="p-1.5 rounded hover:bg-gray-100"
                      title={deck.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {deck.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setPreviewDeck(deck)}
                      className="p-1.5 rounded hover:bg-[#FED7A5]/40 text-[#2D4354]"
                      title="Preview Deck"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteDeckModal({ isOpen: true, deckId: deck.id })}
                      className="p-1.5 rounded hover:bg-red-100 text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm mb-3" style={{ color: '#73766A' }}>{deck.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2" style={{ color: '#9E6752' }}>
                    <Package className="w-4 h-4" />
                    <span>{deck.product_ids?.length || 0} products</span>
                  </div>
                  <span 
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: deck.is_active ? '#D1FAE5' : '#FEE2E2',
                      color: deck.is_active ? '#065F46' : '#991B1B'
                    }}
                  >
                    {deck.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* ===== FEATURED SECTIONS ===== */}
      {activeTab === 'featured' && (
        <div className="bg-white rounded-xl p-4 sm:p-6" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#FED7A5' }} />
                Featured Sections ({featuredSections.length})
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Highlighted collections that appear on browse page</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {featuredSections.length > 0 && (
                <Button
                  onClick={toggleSelectAllFeaturedSections}
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm hover:bg-[#FED7A5] hover:text-[#20212B] hover:border-[#9E6752] transition-colors hover-amber"
                  style={{ color: '#534145', borderColor: '#534145' }}
                >
                  {selectedFeaturedSections.length === featuredSections.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
              <Button
                data-testid="ai-create-featured-btn"
                onClick={() => { setAiFeaturedBrief(''); setShowAIFeatured(true); }}
                variant="outline"
                className="text-xs sm:text-sm px-3 sm:px-4"
                style={{ borderColor: '#9E6752', color: '#9E6752' }}
              >
                <Wand2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                AI Create
              </Button>
              <Button
                onClick={() => {
                  resetFeaturedForm();
                  setShowFeaturedDialog(true);
                }}
                style={{ background: '#9E6752', color: 'white' }}
                className="text-xs sm:text-sm px-3 sm:px-4"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Create Featured Section</span>
                <span className="sm:hidden">Create Featured</span>
              </Button>
            </div>
          </div>

        {/* Bulk Actions Toolbar for Featured Sections */}
        {selectedFeaturedSections.length > 0 && (
          <div className="mb-4 px-4 py-3 bg-[#FFF8F0] border border-[#FED7A5] rounded-lg flex items-center justify-between">
            <span className="text-sm font-medium text-[#2D4354]">
              {selectedFeaturedSections.length} section{selectedFeaturedSections.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setSelectedFeaturedSections([])}
                variant="outline"
                size="sm"
              >
                Clear
              </Button>
              <Button
                onClick={() => handleBulkToggleFeaturedSections(true)}
                variant="outline"
                size="sm"
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <Eye className="w-4 h-4 mr-1" />
                Activate All
              </Button>
              <Button
                onClick={() => handleBulkToggleFeaturedSections(false)}
                variant="outline"
                size="sm"
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <EyeOff className="w-4 h-4 mr-1" />
                Deactivate All
              </Button>
              <Button
                onClick={() => setBulkDeleteFeaturedModal(true)}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete All
              </Button>
            </div>
          </div>
        )}

        {featuredSections.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg" style={{ borderColor: '#E5E5E5' }}>
            <Sparkles className="w-12 h-12 mx-auto mb-3" style={{ color: '#FED7A5', opacity: 0.5 }} />
            <p className="text-gray-600">No featured sections created yet</p>
            <p className="text-sm text-gray-500">Create curated collections to highlight on the browse page</p>
          </div>
        ) : (
          <div className="space-y-3">
            {featuredSections.map(section => (
              <div key={section.id} className={`border-2 rounded-lg p-4 transition-all ${selectedFeaturedSections.includes(section.id) ? 'border-[#9E6752] bg-[#FFF8F0]' : 'border-gray-200 hover:border-[#FED7A5]'}`}>
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedFeaturedSections.includes(section.id)}
                      onChange={() => toggleSelectFeaturedSection(section.id)}
                      className="w-4 h-4 rounded border-gray-300 mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg" style={{ color: '#20212B' }}>{section.name}</h3>
                        <span className="text-xs px-2 py-1 rounded-full" style={{ background: '#FED7A5', color: '#534145' }}>
                          {section.category_name}
                        </span>
                      </div>
                      <p className="text-sm mb-2" style={{ color: '#73766A' }}>{section.description}</p>
                      <div className="flex items-center gap-4 text-sm" style={{ color: '#9E6752' }}>
                        <span>{section.item_ids?.length || 0} items</span>
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: section.is_active ? '#D1FAE5' : '#FEE2E2',
                            color: section.is_active ? '#065F46' : '#991B1B'
                          }}
                        >
                          {section.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleToggleFeatured(section.id)}
                      className="p-2 rounded hover:bg-gray-100"
                      title={section.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {section.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => window.open(`/collection/${section.id}`, '_blank')}
                      className="p-2 rounded hover:bg-[#FED7A5]/40 text-[#2D4354]"
                      title="View shareable collection page"
                      data-testid={`featured-view-collection-${section.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async () => {
                        const url = `${window.location.origin}/collection/${section.id}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success(section.is_active
                            ? 'Shareable link copied! Your affiliate links are baked in.'
                            : 'Link copied, but activate the section first so visitors can see it.');
                        } catch {
                          toast.error('Could not copy, link: ' + url);
                        }
                      }}
                      className="p-2 rounded hover:bg-[#FED7A5]/40 text-[#9E6752]"
                      title="Copy shareable 'Shop this collection' link"
                      data-testid={`featured-share-link-${section.id}`}
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditFeatured(section)}
                      className="p-2 rounded hover:bg-gray-100"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteFeaturedModal({ isOpen: true, sectionId: section.id })}
                      className="p-2 rounded hover:bg-red-100 text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* ===== CRAFT DECK DIALOG ===== */}
      <DeckFormModal
        open={showDeckDialog}
        onOpenChange={setShowDeckDialog}
        deckFormData={deckFormData}
        setDeckFormData={setDeckFormData}
        products={products}
        decks={decks}
        selectedProductsForDeck={selectedProductsForDeck}
        handleProductToggleForDeck={handleProductToggleForDeck}
        handleCreateDeck={handleCreateDeck}
      />

      {/* ===== AI CREATE FEATURED SECTION DIALOG ===== */}
      <Dialog open={showAIFeatured} onOpenChange={(o) => { if (!aiFeaturedLoading) setShowAIFeatured(o); }}>
        <DialogContent className="max-w-md admin-area" data-testid="ai-featured-dialog">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            <Wand2 className="w-5 h-5" style={{ color: '#9E6752' }} />
            AI Create Featured Section
          </DialogTitle>
          <DialogDescription>
            Describe the theme and the AI will name the section, write its description,
            pick a browse category, and select up to 4 products or decks from your catalog.
            You review everything before saving.
          </DialogDescription>
          <Textarea
            data-testid="ai-featured-brief-input"
            placeholder='e.g. "cozy fall evenings at home" or "gifts for plant parents"'
            value={aiFeaturedBrief}
            onChange={(e) => setAiFeaturedBrief(e.target.value)}
            rows={3}
            disabled={aiFeaturedLoading}
          />
          <Button
            data-testid="ai-featured-generate-btn"
            onClick={handleAIFeatured}
            disabled={aiFeaturedLoading}
            className="w-full"
            style={{ background: '#9E6752', color: 'white' }}
          >
            {aiFeaturedLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {aiFeaturedLoading ? 'Curating...' : 'Generate section'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* ===== FEATURED SECTION DIALOG ===== */}
      <FeaturedSectionFormModal
        open={showFeaturedDialog}
        onOpenChange={setShowFeaturedDialog}
        editingFeatured={editingFeatured}
        featuredFormData={featuredFormData}
        setFeaturedFormData={setFeaturedFormData}
        browseSections={browseSections}
        selectedItems={selectedItems}
        selectedItemTypes={selectedItemTypes}
        showItemSelector={showItemSelector}
        setShowItemSelector={setShowItemSelector}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        products={products}
        decks={decks}
        handleItemToggle={handleItemToggle}
        handleCreateFeaturedSection={handleCreateFeaturedSection}
        getItemName={getItemName}
      />

      <DeleteConfirmationModal
        isOpen={deleteDeckModal.isOpen}
        onClose={() => setDeleteDeckModal({ isOpen: false, deckId: null })}
        onConfirm={() => handleDeleteDeck(deleteDeckModal.deckId)}
        title="Delete Deck"
        message="Are you sure you want to delete this deck? This action cannot be undone."
      />

      <DeleteConfirmationModal
        isOpen={deleteFeaturedModal.isOpen}
        onClose={() => setDeleteFeaturedModal({ isOpen: false, sectionId: null })}
        onConfirm={() => handleDeleteFeatured(deleteFeaturedModal.sectionId)}
        title="Delete Featured Section"
        message="Are you sure you want to delete this featured section? This action cannot be undone."
      />

      {/* Deck Preview Modal */}
      {previewDeck && (
        <Dialog open={true} onOpenChange={() => setPreviewDeck(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto admin-area">
            <div className="py-6">
              <div className="text-center mb-8">
                <DialogTitle className="text-3xl sm:text-4xl" style={{fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: '600'}}>
                  {previewDeck.title}
                </DialogTitle>
                <DialogDescription className="text-base mt-2" style={{color: '#73766A'}}>
                  {previewDeck.description}
                </DialogDescription>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {previewDeck.product_ids?.map(productId => {
                  const product = products.find(p => p.id === productId);
                  return product ? (
                    <ProductCard key={product.id} product={product} user={null} />
                  ) : null;
                })}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Delete Decks Confirmation Modal */}
      <ConfirmModal
        isOpen={bulkDeleteDecksModal}
        onClose={() => setBulkDeleteDecksModal(false)}
        onConfirm={handleBulkDeleteDecks}
        title="Delete Multiple Decks"
        message={`Are you sure you want to delete ${selectedDecks.length} decks? This action cannot be undone.`}
        confirmText="Delete All"
        confirmStyle="danger"
      />

      {/* Bulk Delete Featured Sections Confirmation Modal */}
      <ConfirmModal
        isOpen={bulkDeleteFeaturedModal}
        onClose={() => setBulkDeleteFeaturedModal(false)}
        onConfirm={handleBulkDeleteFeaturedSections}
        title="Delete Multiple Featured Sections"
        message={`Are you sure you want to delete ${selectedFeaturedSections.length} featured sections? This action cannot be undone.`}
        confirmText="Delete All"
        confirmStyle="danger"
      />
    </div>
  );
}
